export const CLUB_DE_TRADING_COURSE_ID = '3';
export const CLUB_DE_TRADING_COURSE_NAME = 'club de trading';
export const CLUB_DE_TRADING_HUB_URL = 'https://hub.elclubtrading.com/';

export const MARATON_DE_INVERSIONES_COURSE_ID = '2';
export const MARATON_DE_INVERSIONES_COURSE_NAME = 'maratón de inversiones';
export const MARATON_DE_INVERSIONES_PORTAFOLIOS_URL = 'https://cartera.inversapiens.com/';

export const isClubDeTradingCourse = (course?: { id?: string; name?: string } | null): boolean => {
  if (!course) return false;
  if (course.id === CLUB_DE_TRADING_COURSE_ID) return true;
  return course.name?.trim().toLowerCase() === CLUB_DE_TRADING_COURSE_NAME;
};

export const isMaratonDeInversionesCourse = (course?: { id?: string; name?: string } | null): boolean => {
  if (!course) return false;
  if (String(course.id) === MARATON_DE_INVERSIONES_COURSE_ID) return true;
  return course.name?.trim().toLowerCase() === MARATON_DE_INVERSIONES_COURSE_NAME;
};

export interface CourseTheme {
  main: string;
  dark: string;
  gradient: string;
  tint: string;
  tintBorder: string;
  /** Logo blanco para poner sobre el color del curso */
  logo?: string;
}

const DEFAULT_COURSE_THEME: CourseTheme = {
  main: '#7c3aed',
  dark: '#50378a',
  gradient: 'linear-gradient(120deg, #50378a 0%, #7c3aed 60%, #a855f7 100%)',
  tint: '#f5f3ff',
  tintBorder: '#e9d8fd',
};

// Mezcla un color hacia otro (negro, blanco o gris) para armar los tonos del curso
const mixTo = (hex: string, target: number[], amount: number) => {
  const channels = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `#${channels.map((c, i) => Math.round(c + (target[i] - c) * amount).toString(16).padStart(2, '0')).join('')}`;
};
const BLACK = [0, 0, 0];
const WHITE = [255, 255, 255];
const SLATE = [100, 116, 139];

// Colores más apagados (30% hacia el gris) y planos, sin degradado: Carlos los eligió el 31-10 sobre los saturados
const themeFromColor = (color: string, logo?: string): CourseTheme => {
  const main = mixTo(color, SLATE, 0.3);
  return {
    main,
    dark: mixTo(main, BLACK, 0.25),
    gradient: main,
    tint: mixTo(main, WHITE, 0.93),
    tintBorder: mixTo(main, WHITE, 0.75),
    logo,
  };
};

// Club y Maratón: el color de su logo (toro azul, verde menta), con el logo en blanco para los encabezados
const CLUB_DE_TRADING_THEME = themeFromColor('#0b47a8', '/assets/images/logo/cursos/club-de-trading-blanco.png');
const MARATON_DE_INVERSIONES_THEME = themeFromColor('#0f9b80', '/assets/images/logo/cursos/maraton-de-inversiones-blanco.png');

// Resto de los cursos: el color de su portada en la Academia (por id del curso; uno nuevo queda en morado)
const COURSE_COLORS: Record<string, string> = {
  '4': '#6d28d9', // Criptomonedas Primeros Pasos (violeta)
  '6': '#c62828', // Entrenamiento Trading 2025 (rojo de la "TR")
  '7': '#dd4a12', // Deja de Improvisar tu Vida (naranjo)
  '8': '#1e40af', // Un Sistema de Trading Consistente (azul rey)
  '9': '#c62828', // Entrenamiento Trading 2026 (Marzo)
  '10': '#2f8f2a', // Entradas, Emociones y Salidas (verde)
  '11': '#0e5a78', // Inversionista de 0 a 100 (azul petróleo)
  '12': '#1576d6', // Tu primer portafolio de ETFs (azul eléctrico)
  '13': '#c62828', // Entrenamiento Trading 2026 Octubre
  '14': '#0f766e', // Dispara tus ingresos con IA (verde azulado)
};

/** Tema propio del curso, o null si el curso no tiene color propio (usa el morado de la Academia). */
export const getBrandedCourseTheme = (course?: { id?: string; name?: string } | null): CourseTheme | null => {
  if (isClubDeTradingCourse(course)) return CLUB_DE_TRADING_THEME;
  if (isMaratonDeInversionesCourse(course)) return MARATON_DE_INVERSIONES_THEME;
  const color = course?.id !== undefined ? COURSE_COLORS[String(course.id)] : undefined;
  return color ? themeFromColor(color) : null;
};

export const getCourseTheme = (course?: { id?: string; name?: string } | null): CourseTheme =>
  getBrandedCourseTheme(course) ?? DEFAULT_COURSE_THEME;

export type CourseKind = 'membership' | 'training' | 'recorded';

export const COURSE_KIND_LABEL: Record<CourseKind, string> = {
  membership: 'Membresía',
  training: 'Entrenamiento',
  recorded: 'Curso grabado',
};

/** Tipo de curso para agrupar en el admin: Club y Maratón son membresías; "Entrenamiento…" en el nombre, entrenamiento; el resto, grabado. */
export const getCourseKind = (course?: { id?: string; name?: string } | null): CourseKind => {
  if (isClubDeTradingCourse(course) || isMaratonDeInversionesCourse(course)) return 'membership';
  if (/entrenamiento/i.test(course?.name ?? '')) return 'training';
  return 'recorded';
};
